import { useEffect, useState } from "react";
import { Checkbox, Box } from "@chakra-ui/react";
import { IHeaderParams } from "@ag-grid-community/core";

export const CheckboxHeaderComponent = (props: IHeaderParams) => {
    const [checked, setChecked] = useState(false);
    const [indeterminate, setIndeterminate] = useState(false);
    const api = props.api;

    // Update checkbox state when selection changes
    useEffect(() => {
        if (!api) return;

        const updateCheckboxState = () => {
            const selectedNodes = api.getSelectedNodes();
            const displayedRowCount = api.getDisplayedRowCount();

            if (selectedNodes.length === 0) {
                setChecked(false);
                setIndeterminate(false);
            } else if (selectedNodes.length === displayedRowCount && displayedRowCount > 0) {
                setChecked(true);
                setIndeterminate(false);
            } else {
                setChecked(false);
                setIndeterminate(true);
            }
        };

        // Initial state - with a small delay to ensure data is loaded
        setTimeout(updateCheckboxState, 200);

        // Subscribe to selection changes
        const selectionChangedListener = () => {
            updateCheckboxState();
        };

        const modelUpdatedListener = () => {
            updateCheckboxState();
        };

        // Add listener for row data changes (including deletions)
        const rowDataChangedListener = () => {
            updateCheckboxState();
        };

        // Add listener for cell value changes
        const cellValueChangedListener = () => {
            updateCheckboxState();
        };

        api.addEventListener("selectionChanged", selectionChangedListener);
        api.addEventListener("modelUpdated", modelUpdatedListener);
        api.addEventListener("rowDataChanged", rowDataChangedListener);
        api.addEventListener("cellValueChanged", cellValueChangedListener);

        return () => {
            api.removeEventListener("selectionChanged", selectionChangedListener);
            api.removeEventListener("modelUpdated", modelUpdatedListener);
            api.removeEventListener("rowDataChanged", rowDataChangedListener);
            api.removeEventListener("cellValueChanged", cellValueChangedListener);
        };
    }, [api]);

    const onCheckboxChange = () => {
        if (!api) return;

        if (checked || indeterminate) {
            // If checked or indeterminate, deselect all
            api.deselectAll();
            setChecked(false);
            setIndeterminate(false);
        } else {
            // For infinite row model, we need to select all visible rows manually
            // but only if they have valid IDs
            const rowCount = api.getDisplayedRowCount();

            // Use a try-catch block to handle potential errors
            try {
                for (let i = 0; i < rowCount; i++) {
                    const rowNode = api.getDisplayedRowAtIndex(i);
                    // Only select nodes that have data and an ID
                    if (rowNode && rowNode.data && rowNode.id !== undefined) {
                        rowNode.setSelected(true);
                    }
                }
                // Update state after successful selection
                setChecked(true);
                setIndeterminate(false);
            } catch (error) {
                console.warn("Error selecting rows:", error);
                // If there's an error, don't update the checkbox state
            }
        }
    };

    return (
        <Box display="flex" height="100%" alignItems="center" justifyContent="center">
            <Checkbox isChecked={checked} isIndeterminate={indeterminate} onChange={onCheckboxChange} aria-label="Select all rows" />
        </Box>
    );
};
